/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { AppThunk } from "../../../config/store";
import { CriticalResultAction } from "./slices";
import { TabKey } from "../CriticalResultTabContainer";
import { criticalResultApi, orderApi } from "../../../config/api";
import {
  SearchPrams,
  CriticalResultSearchState,
} from "@src/types/criticalResult";
import { axiosInstanceRegion } from "../../../config/axios-region";
import {
  DEFAULT_TIMEZONE,
  IDiagnosticReportContent,
  initSlicePageable,
  isViewAsTextContent,
} from "@advapacs/common";

export const getCriticalResultList =
  (tabKey: string, props: SearchPrams): AppThunk =>
  async (dispatch) => {
    try {
      let requestParams = {};
      const {
        page,
        size = initSlicePageable.size,
        patientName,
        patientId,
        accessionNumber,
        customers,
        statuses,
      } = props;
      if (page) requestParams = { ...requestParams, page };
      if (size) requestParams = { ...requestParams, size };
      if (patientName) {
        requestParams = {
          ...requestParams,
          patientName: patientName.trim(),
        };
      }
      if (patientId) {
        requestParams = {
          ...requestParams,
          patientId: patientId.trim(),
        };
      }
      if (accessionNumber) {
        requestParams = {
          ...requestParams,
          accessionNumber,
        };
      }
      if (statuses && statuses.length > 0) {
        requestParams = {
          ...requestParams,
          statuses: statuses.toString(),
        };
      }

      if (customers && customers.length > 0) {
        requestParams = {
          ...requestParams,
          clinicianPortalIds: customers.toString(),
        };
      }
      dispatch(CriticalResultAction.getListStart());
      const url =
        TabKey.MY_CASE === tabKey
          ? criticalResultApi.GET_OWN
          : criticalResultApi.GET_TO_ME;
      const response = await axiosInstanceRegion.get(url, {
        params: requestParams,
      });
      dispatch(CriticalResultAction.getListSuccess(response.data));
    } catch (err) {
      dispatch(CriticalResultAction.getListFail(err?.response?.data));
    }
  };

export const getCriticalResultDetail =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await axiosInstanceRegion.get(
        criticalResultApi.GET_CRITICAL_RESULT(id)
      );
      dispatch(CriticalResultAction.getDetailSuccess(response.data));
    } catch (err) {
      dispatch(CriticalResultAction.getDetailFail(err?.response?.data));
    }
  };

export const logCriticalResultViewDetail =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      await axiosInstanceRegion.put(
        criticalResultApi.GET_CRITICAL_RESULT_VIEW(id)
      );
    } catch (err) {
      dispatch(CriticalResultAction.getDetailFail(err?.response?.data));
    }
  };

export const getCriticalResultCount = (): AppThunk => async (dispatch) => {
  try {
    const response = await axiosInstanceRegion.get(
      criticalResultApi.GET_CRITICAL_COUNT
    );
    dispatch(CriticalResultAction.getCountSuccess(response?.data?.count));
  } catch (err) {
    dispatch(CriticalResultAction.getCountFail(err?.response?.data));
  }
};

export const getStudiesByCR =
  (id: string, isMyCase = false): AppThunk =>
  async (dispatch) => {
    try {
      const params = {
        isMyCase: isMyCase,
      };
      const response = await axiosInstanceRegion.get(
        criticalResultApi.GET_CRITICAL_RESULT_STUDIES(id),
        { params }
      );
      dispatch(CriticalResultAction.getStudyReportSuccess(response.data));
    } catch (err) {
      dispatch(CriticalResultAction.getStudyReportFail(err?.response?.data));
    }
  };

export const markOrderViewed =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      await axiosInstanceRegion.put(
        criticalResultApi.CRITICAL_RESULT_VIEWED(id)
      );
    } catch (err) {
      dispatch(CriticalResultAction.setErrorMessage(err?.response?.data));
    }
  };

export const orderDownloadPdfReport = async (
  portalId: string,
  patientId: string,
  orderId: string,
  content: IDiagnosticReportContent,
  report: boolean
) => {
  const contentId = content?.id;
  const url = report
    ? orderApi.ORDER_GENERATE_PDF_REPORT_PDF(
        portalId,
        patientId,
        orderId,
        contentId
      )
    : orderApi.ORDER_GET_DIAGNOSTIC_REPORT_CONTENT(
        portalId,
        patientId,
        orderId,
        contentId
      );
  const params: Record<string, string> = {
    origin: window.origin,
    download: "true",
    "x-tz": DEFAULT_TIMEZONE.value,
  };
  const viewAsText = isViewAsTextContent(content);
  if (!report && viewAsText) {
    params.viewAsText = "true";
  }
  const urlParams = new URLSearchParams(params);
  const SERVER_API_URL = axiosInstanceRegion.defaults.baseURL;
  window.open(`${SERVER_API_URL}${url}?${urlParams.toString()}`, "_blank");
};

export const getCriticalResultSearchParams = (
  searchState: CriticalResultSearchState
): SearchPrams => {
  return {
    statuses: searchState?.statuses?.map((status) => status.value),
    customers: searchState?.customers?.map((id) => id.value),
    patientName: searchState?.patientName,
    isFuzzySearch: searchState?.isFuzzySearch || false,
    patientId: searchState.patientId,
    accessionNumber: searchState?.accessionNumber,
    page: searchState?.page ? searchState?.page : null,
  };
};
